import React, { useState } from 'react';
import { Form, Input, Button, DatePicker, TimePicker, Select, message, Typography } from 'antd';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

const { Option } = Select;
const { Title } = Typography;

const CreateGameForm = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const onFinish = async (values) => {
    const homeScore = values.home_score !== undefined ? parseInt(values.home_score, 10) : null;
    const awayScore = values.away_score !== undefined ? parseInt(values.away_score, 10) : null;

    // Check if scores are provided and not valid integers
    if (
      (values.home_score !== undefined && isNaN(homeScore)) ||
      (values.away_score !== undefined && isNaN(awayScore))
    ) {
      message.error('Home and Away scores must be valid integers if provided.');
      return;
    }

    setLoading(true);

    try {
      const formattedValues = {
        ...values,
        date: values.date.format('YYYY-MM-DD'),
        time: values.time.format('HH:mm:ss'),
      };

      const access_token = localStorage.getItem('access_token');

      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/games/create/`, formattedValues, {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      });

      message.success('Game created successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error creating game:', error);

      // Display the error message from the backend
      if (error.response && error.response.data) {
        const backendErrorMessage = Object.values(error.response.data)[0];
        message.error(backendErrorMessage || 'Failed to create the game.');
      } else {
        message.error('Failed to create the game.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        padding: '60px 20px 20px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start', // Ensures the card is positioned correctly
      }}
    >
      <div
        style={{
          maxWidth: '600px',
          width: '100%',
          padding: '20px',
          background: '#f7f7f7',
          borderRadius: '8px',
          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Title level={3} style={{ textAlign: 'center', color: '#1890ff', marginBottom: '20px' }}>
          Create New Game
        </Title>
        <Form
          name="create-game"
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            date: moment(),
            time: moment(),
          }}
        >
          {/* Game Type */}
          <Form.Item
            name="game_type"
            label="Game Type"
            rules={[{ required: true, message: 'Please select the game type' }]}
          >
            <Select placeholder="Select a game type">
              <Option value="league">League</Option>
              <Option value="friendly">Friendly</Option>
              <Option value="cup">Cup</Option>
            </Select>
          </Form.Item>

          {/* Location */}
          <Form.Item
            name="location"
            label="Location"
            rules={[
              { required: true, message: 'Please enter the game location' },
              { type: 'string', message: 'Location must be a string' },
            ]}
          >
            <Input placeholder="Enter game location" />
          </Form.Item>

          {/* Date */}
          <Form.Item
            name="date"
            label="Date"
            rules={[{ required: true, message: 'Please select the game date' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          {/* Time */}
          <Form.Item
            name="time"
            label="Time"
            rules={[{ required: true, message: 'Please select the game time' }]}
          >
            <TimePicker use12Hours format="h:mm a" style={{ width: '100%' }} />
          </Form.Item>

          {/* Home Team */}
          <Form.Item
            name="home_team"
            label="Home Team"
            rules={[
              { required: true, message: 'Please enter the home team name' },
              { type: 'string', message: 'Home team name must be a string' },
            ]}
          >
            <Input placeholder="Enter home team name" />
          </Form.Item>

          {/* Away Team */}
          <Form.Item
            name="away_team"
            label="Away Team"
            rules={[
              { required: true, message: 'Please enter the away team name' },
              { type: 'string', message: 'Away team name must be a string' },
            ]}
          >
            <Input placeholder="Enter away team name" />
          </Form.Item>

          {/* Home Score */}
          <Form.Item
            name="home_score"
            label="Home Score"
          >
            <Input placeholder="Optional" />
          </Form.Item>

          {/* Away Score */}
          <Form.Item
            name="away_score"
            label="Away Score"
          >
            <Input placeholder="Optional" />
          </Form.Item>

          {/* Submit Button */}
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{
                width: '100%',
                height: '40px',
                borderRadius: '5px',
                background: 'linear-gradient(135deg, #1890ff 0%, #722ed1 100%)',
                color: 'white',
                fontWeight: 'bold',
              }}
            >
              Create Game
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default CreateGameForm;
